import { useState } from 'react';
import { PaperIcon } from './SVGIcons';

// ── Project data ──────────────────────────────────────────────────────────────

const PROJECTS = [
  {
    id: 'hyperlite',
    title: 'HyperLite',
    subtitle: 'A Local AI Assistant That Stays on Your Machine',
    icon: <PaperIcon />,
    url: 'https://hyperlite.org',
    tags: ['Rust', 'ratatui', 'llama.cpp', 'SQLite', 'ONNX', 'GGUF'],
    sections: [
      {
        heading: 'Overview',
        body: `Most AI assistants in common use today are services. You type into a window, your text travels to a company's servers, a model there produces a reply, and it travels back. The arrangement is convenient and, for many people, entirely acceptable. It also means a recurring bill, an account, a network dependency, and a degree of trust that whatever you typed is handled responsibly on the other end. HyperLite is built on the opposite assumption: that the model should run where you are, on hardware you control, and that nothing you say to it needs to leave the room.

It is a terminal application written in Rust. That description alone tells a technical reader most of what they need to know about its priorities — it is a single compiled binary, it starts quickly, it uses little memory of its own, and it lives alongside the files and shell a developer already works in rather than in a separate browser tab. For everyone else, the practical summary is simpler: you install it with one command, you run it with one command, and from that point it works offline, with no sign-up and no subscription.`,
      },
      {
        heading: 'Install',
        body: `npm install -g hyperlite-ai
hl

Distributed through npm for convenience — but it does not require Node.js to run, and it is not an Electron or web application. The package pulls down the correct native binary for your platform as an optional dependency.`,
      },
      {
        heading: 'What It Does',
        body: `At its center is an ordinary chat interface. You ask a question or give an instruction, and a language model answers. The model itself is one you choose and download; HyperLite does not ship a model of its own but rather acts as the program that runs them. It supports the widely used GGUF model format and connects to essentially every local inference engine in circulation — Ollama, llama.cpp, LM Studio, Jan, KoboldCpp, vLLM, GPT4All, LocalAI, llamafile, and others — so it tends to fit into whatever setup a user already has rather than demanding a particular one.

Where it goes beyond a simple chat window is in what it can do with the conversation. The assistant has access to more than thirty built-in tools:

> Read and write files, run shell commands
> Search the web and fetch pages
> Extract text from PDFs, analyze spreadsheets
> Perform Git operations and report on system status

These actions are not reserved for models with formal function-calling support — HyperLite reads the model's output, recognizes when it is requesting a tool, and carries the request out. The notable restraint is on file changes: any edit the assistant proposes is shown first as a line-by-line, syntax-highlighted difference, and it is not written to disk until you approve it. The program is willing to do work on your behalf, but it does not do so silently.

The work it does persists. Conversations are stored in a local SQLite database and can be searched and resumed later. Folders can be indexed so the assistant can search a codebase or document set by meaning rather than exact wording — those embeddings are computed on the machine itself using a local ONNX model, with no external service involved. Facts you ask it to remember carry across sessions. None of this involves a server.`,
      },
      {
        heading: 'Fitting the Hardware',
        body: `The practical obstacle to running AI locally has always been that capable models are large and the hardware to run them varies enormously. On first launch HyperLite inspects the processor, memory, and graphics card, installs an appropriate inference engine, and presents a curated list of about thirty models filtered to those that will actually run on what it found.

That list is organized into three tiers that correspond to how people actually own hardware:

> Small and very small models — single-board computers, laptops, and CPU-only machines
> Mid-range models — the 8–16 GB graphics cards that make up the bulk of consumer desktops
> Larger models — high-end cards with 24–32 GB

Each entry lists its download size and the precision it was quantized to, so the choice is informed rather than a guess, and every entry is checked against its source so a download does not fail halfway through on a path that no longer exists. The same program is useful on a Raspberry Pi and on a workstation with a recent high-end card, without asking the user to understand quantization formats or memory budgets to get started.`,
      },
      {
        heading: 'The Design Underneath',
        body: `The technical choices are conservative in a way that is worth noting, because conservatism is the point.

> Interface built on the established Rust terminal libraries (ratatui, crossterm) rather than a bespoke rendering layer, with async I/O on tokio
> Networking uses rustls rather than carrying an OpenSSL dependency
> State lives in a bundled SQLite database — the most widely deployed database in the world — rather than a custom format
> Embeddings run through the ONNX runtime (fastembed); document handling uses ordinary, well-understood libraries for PDF and CSV parsing
> An optional build compiles llama.cpp directly into the binary for in-process GGUF inference, but the default keeps the model engine a separate, swappable component

None of these decisions is novel, and that is their value. The release binary is optimized, stripped, and built to abort on panic rather than unwind, which keeps it small and its failure behavior simple. The program makes no telemetry calls and no analytics calls. The only network traffic it generates is to the local inference engine, to package registries when you choose to download a model, and to the open web when you explicitly invoke a search or fetch. There is nothing else going out, because there is nowhere else it is trying to go.`,
      },
      {
        heading: 'Who It Is For',
        body: `HyperLite will not appeal to everyone, and it does not try to. It assumes a user who is comfortable, or willing to become comfortable, opening a terminal. In exchange it offers something the hosted services structurally cannot: an assistant that costs nothing to run after installation, that works on an airplane or behind an air-gapped network, and that handles sensitive code or private documents without any of it being transmitted anywhere.

For a developer, it sits naturally inside an existing workflow and can act on the project at hand. For a more casual user, it is a capable assistant that happens to keep its data to itself. It is distributed under the MIT license — free to use, free to inspect, and free to build upon. The premise from the first command to the last is that the software, the model, and the conversation all belong to the person running them.`,
      },
      {
        heading: 'Tech Stack',
        body: `Language & Runtime
> Rust — ships as a single self-contained native binary per platform, no runtime dependency at execution time

Interface
> ratatui + crossterm terminal UI, async I/O on tokio
> Syntax highlighting via syntect, Markdown via pulldown-cmark

Inference
> Connects to local servers over HTTP (reqwest + rustls)
> Optional compile-time feature embeds llama.cpp for in-process GGUF inference

Storage & Retrieval
> Bundled SQLite for conversations, memory, and configuration in ~/.hyperlite/
> On-device embeddings via fastembed (ONNX runtime)

Documents & Web
> PDF extraction via pdf-extract, CSV via the csv crate, HTML parsing via scraper

Platforms
> Linux x64 (GPU via Ollama/CUDA), macOS Apple Silicon (Metal llama.cpp), macOS Intel (CPU), Windows 10/11 x64 (pre-built GPU binary)`,
      },
    ],
  },
  {
    id: 'thot-leader',
    title: 'The Thot Leader',
    subtitle: "America's Most Inaccurate News Source",
    icon: <PaperIcon />,
    url: 'https://thethotleader.com',
    tags: ['Next.js 15', 'Firebase', 'Claude API', 'DALL-E 3', 'Tailwind CSS'],
    sections: [
      {
        heading: 'Overview',
        body: `The Thot Leader is a fully automated AI-powered satirical news site styled as a hyper-patriotic American cable news network. Every article on the site is real — pulled from live headlines, rewritten by Claude into biting satire, illustrated with a custom AI-generated image, and published automatically on a schedule. Zero human editorial involvement. The joke writes itself, then deploys itself.`,
      },
      {
        heading: 'The Pipeline',
        body: `The core is a Firebase Cloud Function on a cron schedule that fires every 48 hours:

> Fetches current top headlines from TheNewsAPI across four categories — breaking news, politics, arts & music, and sports
> Deduplicates against existing Firestore articles to avoid reprints
> Sends each headline to Claude (claude-sonnet-4-6) with a prompt engineered for dark, punk-inflected satire — think The Onion written by someone who's read too much Baudrillard
> Parses the response, validates it isn't a refusal or error, and extracts the headline and article body
> Calls DALL-E 3 with a topic-safe cartoon illustration prompt to generate a cover image
> Downloads the DALL-E image (which expires after ~1 hour), uploads it permanently to Firebase Storage, and stores the firebasestorage.googleapis.com URL
> Writes the final article document to Firestore

The entire pipeline runs serverlessly with no human in the loop.`,
      },
      {
        heading: 'The Frontend',
        body: `Built with Next.js 15 as a fully static export (output: 'export'), deployed to Firebase Hosting. There is no server — the built out/ directory is pure static HTML/CSS/JS. Articles are fetched client-side from a getArticles Cloud Function endpoint on every page load, so the feed is always current without a rebuild.

The visual design leans hard into the bit: deep navy and crimson cable news aesthetic with Oswald headlines, a live breaking news ticker that scrolls continuously via CSS animation, a pulsing LIVE dot, fake patriot polls, a Freedom Index™, and satirical sidebar ads for MyPillow and gold bars. The masthead font scales with clamp() from 2rem to 8rem. The whole thing is responsive — sidebar widgets collapse to a two-column grid on mobile, the nav becomes a hamburger menu.`,
      },
      {
        heading: 'Interesting Engineering Decisions',
        body: `Permanent image storage. DALL-E 3 URLs expire roughly one hour after generation. Early versions stored them directly — articles would show broken images by the next day. The fix was uploadImageToStorage(): after generating an image, the function downloads the binary, uploads it to Firebase Storage as a permanent object, and stores the firebasestorage.googleapis.com URL instead.

Content moderation routing. Political and military headlines frequently triggered OpenAI's content policy when used literally as image prompts. The solution was a topic-based prompt layer — the function maps the article's topic to a pre-written safe cartoon prompt and optionally appends a sanitized, sensitive-word-stripped excerpt. Rejection rates dropped to near zero.

Model versioning as an outage. The site went dark for months because the originally hardcoded Claude model ID was deprecated. The function was silently returning errors and saving nothing to Firestore. The fix was updating to claude-sonnet-4-6 and moving all API keys out of source code into Firebase Functions environment config and a local .env file.

Firebase Storage rules vs. GCS ACLs. New Firebase projects use uniform bucket-level access, which disables per-object ACLs. Calling file.save({ public: true }) appeared to succeed but the resulting URLs returned 403 in the browser. The correct approach is using the firebasestorage.googleapis.com/v0/b/{bucket}/o/{encodedPath}?alt=media URL format paired with storage rules that allow unauthenticated reads for the article-images/ path.`,
      },
      {
        heading: 'Tech Stack',
        body: `> Next.js 15 — static export, client-side data fetching
> Firebase Cloud Functions (Node 20) — scheduled + HTTP-triggered pipeline
> Firebase Firestore — article storage
> Firebase Storage — permanent AI image hosting
> Firebase Hosting — static site deployment
> Anthropic Claude API (claude-sonnet-4-6) — satire generation
> OpenAI DALL-E 3 — article image generation
> TheNewsAPI — live headline sourcing
> Tailwind CSS + Oswald / Source Serif 4 — cable news visual design`,
      },
      {
        heading: 'What Makes It Interesting',
        body: `> Fully automated editorial pipeline — from real headline to published satirical article with illustration, no human involved
> Engineered around real-world AI API constraints: content moderation, model deprecation, URL expiry, billing limits
> The site's aesthetic is itself the joke — every UI decision (the ticker, the poll, the Freedom Index) is a deliberate satirical choice, not decoration
> Static frontend + serverless backend means near-zero hosting cost regardless of traffic
> The article tone is prompt-engineered: the Claude system prompt specifies punk, dark, intelligent humor and explicitly bans cliché openings like "In a stunning display" — output quality is a function of prompt craft, not just model capability`,
      },
    ],
  },
  {
    id: 'sos-portfolio',
    title: 'S.O.S Portfolio OS',
    subtitle: "This very site — Sean's Operating System",
    icon: <PaperIcon />,
    tags: ['React 18', 'Transformers.js', 'Firebase', 'WebGPU'],
    sections: [
      {
        heading: 'Overview',
        body: `S.O.S (Sean's Operating System) is the portfolio site you're looking at right now. Rather than a standard scrolling page, it's built as a fully interactive retro desktop OS experience — complete with a CRT power-on boot sequence, a branded splash screen, draggable windows, a mobile dock, and a live AI chat assistant running entirely inside the browser. No backend. No API keys. Just the browser and your GPU.`,
      },
      {
        heading: 'The Boot Experience',
        body: `When you first visit, the screen mimics a CRT monitor powering on — the display blooms from a single horizontal line using CSS scaleY animation, then scrolls through a custom BIOS-style boot sequence with phosphor-green text, scanline overlays, and a vignette glow effect. After the boot completes, a full-screen splash logo fades in: a pulsing ☘ clover with the S.O.S wordmark lit in kelly green.

All of this is pure CSS animation — no canvas, no WebGL, no libraries. The scanline effect is a repeating CSS gradient overlay on a fixed pseudo-element.`,
      },
      {
        heading: 'The Desktop OS',
        body: `The main interface is a classic Mac-inspired desktop built with React. On desktop it renders draggable window icons backed by react-draggable, with a trash can that deletes notes when you drag files onto it. Windows open centered on screen, stack on top of each other, and can be moved anywhere.

On mobile, the desktop transforms into a bottom dock with large tap targets for each section. Windows become full-screen overlays that snap between the menu bar and dock. The same component tree handles both layouts — isMobile state drives the conditional rendering.

The menu bar is a CSS grid (1fr auto 1fr) so the ☘ S.O.S logo is always mathematically centered regardless of screen width.`,
      },
      {
        heading: 'The AI Chat — SmolLM2 in the Browser',
        body: `The Chat window runs SmolLM2-360M-Instruct, a 360 million parameter language model, directly inside the browser tab using @huggingface/transformers (Transformers.js v3).

How it works:
> On first open, the model (~180MB, 4-bit quantized) downloads from the HuggingFace CDN and is cached permanently in the browser's Cache API
> The model runs on the device's GPU via WebGPU (Chrome 113+, Edge 113+, Safari 18+), or falls back to WebAssembly on CPU for older browsers
> Responses stream token-by-token using a TextStreamer callback — text appears word by word, just like ChatGPT
> A system prompt gives the model context about Sean's background so it can answer portfolio questions intelligently
> The entire thing is client-side — no API calls leave the browser after the initial model download

This is genuinely cutting-edge: running a quantized LLM inference loop in a browser tab using the device GPU is only possible with the WebGPU API, which reached stable support in 2024.`,
      },
      {
        heading: 'Theme & Visual Design',
        body: `The color palette is kelly green (#4cbb17) on pure black throughout — chosen to evoke phosphor CRT displays from the 1970s–80s. Every glow effect is CSS text-shadow and filter: drop-shadow stacked at multiple radii to simulate the bloom of a real CRT phosphor dot.

The desktop background image shows through a dark green tint (rgba pseudo-element) and a persistent scanline overlay (repeating-linear-gradient on a fixed ::after pseudo-element), giving the whole OS a unified screen-through-glass feeling even in the live application.`,
      },
      {
        heading: 'Tech Stack',
        body: `> React 18 with hooks — all state, effects, and lifecycle
> @huggingface/transformers v3 — browser-native ML inference
> react-draggable — window and icon drag-and-drop
> Firebase Hosting — deployment with COOP/COEP headers for SharedArrayBuffer support
> CSS custom properties — single source of truth for the green/black design system
> Framer Motion (boot/splash) — CRT power-on and logo reveal animations
> No build-time configuration, no environment variables, no backend`,
      },
      {
        heading: 'What Makes It Interesting',
        body: `> A real LLM running in the browser on your GPU — not a gimmick, a working feature
> The entire boot + splash + OS transition is orchestrated with a 3-phase React state machine (boot → splash → os)
> Mobile and desktop share one component tree; layout mode is a single boolean
> The trash can is functional: drag a saved note icon onto it and the file is deleted
> CSS-only CRT effects that hold up at any resolution with zero JavaScript
> Deployed on Firebase with custom security headers enabling WebGPU and SharedArrayBuffer in all supporting browsers`,
      },
    ],
  },
];

// ── Sub-components ────────────────────────────────────────────────────────────

const Tag = ({ children }) => (
  <span style={{
    display: 'inline-block',
    fontSize: '10px',
    padding: '2px 8px',
    border: '1px solid rgba(76,187,23,0.5)',
    color: '#4cbb17',
    fontFamily: "'Courier New', monospace",
    letterSpacing: '0.05em',
    marginRight: '6px',
    marginBottom: '6px',
  }}>
    {children}
  </span>
);

const Section = ({ heading, body }) => (
  <div style={{ marginBottom: '22px' }}>
    <div style={{
      fontSize: '12px',
      fontWeight: 'bold',
      color: '#4cbb17',
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      textShadow: '0 0 6px #4cbb17',
      borderBottom: '1px solid rgba(76,187,23,0.3)',
      paddingBottom: '4px',
      marginBottom: '10px',
    }}>
      {heading}
    </div>
    <div style={{
      fontSize: '12px',
      lineHeight: '1.75',
      color: '#4cbb17',
      whiteSpace: 'pre-wrap',
      opacity: 0.88,
    }}>
      {body}
    </div>
  </div>
);

// ── File list (folder view) ───────────────────────────────────────────────────

const FileList = ({ onOpen }) => (
  <div style={{ padding: '20px', height: '100%', overflow: 'auto', fontFamily: "'Courier New', monospace" }}>
    <div style={{ fontSize: '11px', color: 'rgba(76,187,23,0.5)', letterSpacing: '0.1em', marginBottom: '20px' }}>
      /dev/projects — {PROJECTS.length} items
    </div>

    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
      {PROJECTS.map(p => (
        <button
          key={p.id}
          onClick={() => onOpen(p)}
          style={{
            background: 'none',
            border: '1px solid rgba(76,187,23,0.3)',
            cursor: 'pointer',
            padding: '16px',
            width: '180px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '10px',
            fontFamily: "'Courier New', monospace",
            transition: 'border-color 0.15s, box-shadow 0.15s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.borderColor = '#4cbb17';
            e.currentTarget.style.boxShadow = '0 0 12px rgba(76,187,23,0.25)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = 'rgba(76,187,23,0.3)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div style={{ width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', filter: 'drop-shadow(0 0 6px #4cbb17)' }}>
            {p.icon}
          </div>
          <div style={{ fontSize: '11px', color: '#4cbb17', textAlign: 'center', textShadow: '0 0 5px #4cbb17', lineHeight: 1.4 }}>
            {p.title}
          </div>
          <div style={{ fontSize: '9px', color: 'rgba(76,187,23,0.55)', textAlign: 'center', lineHeight: 1.3 }}>
            {p.subtitle}
          </div>
        </button>
      ))}
    </div>
  </div>
);

// ── Project detail view ───────────────────────────────────────────────────────

const ProjectDetail = ({ project, onBack }) => (
  <div style={{ height: '100%', display: 'flex', flexDirection: 'column', fontFamily: "'Courier New', monospace" }}>
    {/* Breadcrumb header */}
    <div style={{
      flexShrink: 0,
      padding: '8px 16px',
      borderBottom: '1px solid rgba(76,187,23,0.3)',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      background: 'rgba(76,187,23,0.04)',
    }}>
      <button
        onClick={onBack}
        style={{
          background: 'none',
          border: '1px solid rgba(76,187,23,0.5)',
          color: '#4cbb17',
          fontFamily: "'Courier New', monospace",
          fontSize: '11px',
          padding: '3px 10px',
          cursor: 'pointer',
          letterSpacing: '0.05em',
        }}
      >
        ← Back
      </button>
      <span style={{ fontSize: '11px', color: 'rgba(76,187,23,0.55)', letterSpacing: '0.06em' }}>
        /dev/projects/{project.id}
      </span>
      {project.url && (
        <a
          href={project.url}
          target="_blank"
          rel="noreferrer"
          style={{
            marginLeft: 'auto',
            fontSize: '11px',
            color: '#4cbb17',
            fontFamily: "'Courier New', monospace",
            letterSpacing: '0.05em',
            textDecoration: 'none',
            border: '1px solid rgba(76,187,23,0.5)',
            padding: '3px 10px',
          }}
          onMouseEnter={e => e.currentTarget.style.borderColor = '#4cbb17'}
          onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(76,187,23,0.5)'}
        >
          ↗ Visit Site
        </a>
      )}
    </div>

    {/* Scrollable content */}
    <div style={{
      flex: 1,
      overflowY: 'auto',
      padding: '20px',
      WebkitOverflowScrolling: 'touch',
    }}>
      {/* Title block */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{
          fontSize: '18px',
          fontWeight: 'bold',
          color: '#4cbb17',
          textShadow: '0 0 10px #4cbb17, 0 0 20px rgba(76,187,23,0.4)',
          letterSpacing: '0.1em',
          marginBottom: '4px',
        }}>
          {project.title}
        </div>
        <div style={{ fontSize: '12px', color: 'rgba(76,187,23,0.65)', marginBottom: '12px', letterSpacing: '0.05em' }}>
          {project.subtitle}
        </div>
        <div>
          {project.tags.map(t => <Tag key={t}>{t}</Tag>)}
        </div>
      </div>

      {/* Sections */}
      {project.sections.map(s => (
        <Section key={s.heading} heading={s.heading} body={s.body} />
      ))}
    </div>
  </div>
);

// ── Main export ───────────────────────────────────────────────────────────────

const ProjectsContent = () => {
  const [selected, setSelected] = useState(null);

  return selected
    ? <ProjectDetail project={selected} onBack={() => setSelected(null)} />
    : <FileList onOpen={setSelected} />;
};

export default ProjectsContent;
